import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PAYMENT_METHODS, formatMoney, type CreditCustomer } from '@/lib/credit';

interface Props {
  customer: CreditCustomer;
  /** Dette restante du client (nombre positif). */
  debt: number;
  currency: string;
  onConfirm: (paymentMethod: string) => Promise<void>;
  onClose: () => void;
}

/**
 * SettleDebtModal — le marchand indique que « toute la dette est payée ».
 * Un paiement égal à la dette restante est enregistré (dette remise à zéro),
 * avec le moyen de paiement choisi.
 */
export default function SettleDebtModal({ customer, debt, currency, onConfirm, onClose }: Props) {
  const { t } = useTranslation();
  const [method, setMethod] = useState(PAYMENT_METHODS[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setSaving(true);
    setError('');
    try {
      await onConfirm(method);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('crf.save_error', { defaultValue: 'Erreur lors de l\u2019enregistrement' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={saving ? undefined : onClose} />
      <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
        <div className="w-full sm:max-w-md bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70 overflow-hidden">
          <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-checkbox-circle-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-foreground-950 truncate">{t('crf.settle_all', { defaultValue: 'Tout est payé' })}</h3>
                <p className="text-xs text-foreground-500 truncate">{customer.full_name}</p>
              </div>
            </div>
            <button onClick={onClose} disabled={saving} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0 disabled:opacity-40">
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="rounded-xl bg-emerald-50 border border-emerald-200/60 px-4 py-3 text-center">
              <p className="text-xs text-emerald-700">{t('crf.debt_cleared', { defaultValue: 'Dette remise à zéro' })}</p>
              <p className="text-2xl font-bold text-emerald-700">{formatMoney(debt, currency)}</p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">{t('crf.payment_method', { defaultValue: 'Moyen de paiement' })}</label>
              <div className="flex flex-wrap gap-2">
                {PAYMENT_METHODS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMethod(m)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap border ${
                      method === m
                        ? 'bg-emerald-500 text-white border-emerald-500'
                        : 'bg-background-50 border-background-200/70 text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
            )}
          </div>

          <div className="px-5 pb-5 pt-1">
            <button
              onClick={handleConfirm}
              disabled={saving}
              className="w-full h-12 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer flex items-center justify-center gap-2 bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin"></i>{t('crf.saving', { defaultValue: 'Enregistrement...' })}</>
              ) : (
                <><i className="ri-check-double-line"></i>{t('crf.confirm_all_paid', { defaultValue: 'Oui, tout est payé' })}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}